import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { BaseServices } from 'src/app/kuba/kuba.services';
import { FlexiComplianceTemplateService } from './flexi-compliance-template.service';
import {
  CreateComplianceTemplateRequest,
  FlexiComplianceCatalogSection,
  FlexiComplianceCatalogStructure,
  FlexiComplianceCatalogVersion,
  FlexiComplianceTemplateSummary
} from './flexi-compliance-template.models';

/** A wizard-authored question, not yet persisted. */
interface DraftQuestion {
  tempId: number;
  question: string;
  ref: string;
  weight: number;
}

/** A wizard-authored custom topic, not yet persisted. Negative tempId. */
interface DraftTopic {
  tempId: number;
  title: string;
  questions: DraftQuestion[];
}

/**
 * 4-step creation wizard for compliance templates — a real authoring
 * surface, not just catalog pruning:
 *
 *   1. Basics    — name, description, and "Start from": the full catalog
 *                  (prune), BLANK (build everything yourself), or a copy
 *                  of an existing template (server-side deep copy that
 *                  short-circuits straight to the editor).
 *   2. Topics    — tick/untick catalog topics AND add / rename / remove
 *                  your own custom topics.
 *   3. Questions — per-topic: tick catalog questions, and write / edit /
 *                  remove your own questions (under both catalog and
 *                  custom topics).
 *   4. Review    — full picture incl. authored content, then ONE atomic
 *                  POST create.
 *
 * Custom topics carry negative tempIds until the backend assigns real ids
 * on create.
 */
@Component({
  selector: 'flexi-compliance-template-wizard',
  templateUrl: './template-wizard.component.html',
  styleUrls: ['./templates.scss'],
  standalone: false
})
export class TemplateWizardComponent implements OnInit {
  private readonly notifications = inject(NotificationService);

  step = 0;
  readonly lastStep = 3;

  // ---- Step 1: basics ----
  name = '';
  description = '';
  versions: FlexiComplianceCatalogVersion[] = [];
  selectedVersionId: number | null = null;
  versionsLoading = false;

  /**
   * "Start from" source: 'catalog' (prune flow), 'blank' (author
   * everything), or a template id (duplicate + straight to editor).
   */
  source: 'catalog' | 'blank' | number = 'catalog';
  existingTemplates: FlexiComplianceTemplateSummary[] = [];

  get isDuplicating(): boolean { return typeof this.source === 'number'; }
  get isBlank(): boolean { return this.source === 'blank'; }

  // ---- Steps 2–3: catalog picking ----
  structure: FlexiComplianceCatalogStructure | null = null;
  structureLoading = false;
  includedSectionIds = new Set<number>();
  includedItemIds = new Set<number>();

  // ---- Steps 2–3: authored content ----
  customTopics: DraftTopic[] = [];
  /** Authored questions under CATALOG topics, keyed by catalog section id. */
  customQuestionsBySection: Record<number, DraftQuestion[]> = {};
  private tempIdSeq = -1;

  // Topic add/rename inline forms.
  addTopicOpen = false;
  addTopicTitle = '';
  renameTopicTempId: number | null = null;
  renameTopicTitle = '';

  /**
   * Which topic's questions are shown in step 3. Catalog section ids are
   * positive; custom-topic tempIds are negative — the sign disambiguates.
   */
  activeTopicKey: number | null = null;

  // Question add/edit inline form (one open at a time).
  questionFormOpen = false;
  questionDraft: DraftQuestion = { tempId: 0, question: '', ref: '', weight: 1 };
  /** Non-null while editing an existing draft question. */
  editingQuestionTempId: number | null = null;

  saving = false;
  error: string | null = null;

  /** Business this wizard is running under — scopes the "Start from" list. */
  get businessId(): number { return BaseServices.BusinessId; }

  constructor(
    private service: FlexiComplianceTemplateService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.versionsLoading = true;
    this.service.listCatalogVersions().subscribe({
      next: versions => {
        this.versions = versions;
        this.selectedVersionId = versions.find(v => v.isCurrent)?.id ?? versions[0]?.id ?? null;
        this.versionsLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.versionsLoading = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.WIZARD.LOAD_VERSIONS_FAILED');
      }
    });
    // Existing templates populate the "Start from" dropdown. Scoped to this
    // business so another customer's template is never offered as a starting
    // point. Soft-fail — the catalog/blank paths work without them.
    this.service.list(false, this.businessId).subscribe({
      next: rows => {
        this.existingTemplates = rows.filter(r => r.isActive && !r.isDeleted);
      },
      error: () => { this.existingTemplates = []; }
    });
  }

  // ---------- Derived ----------

  get includedCatalogSections(): FlexiComplianceCatalogSection[] {
    if (this.isBlank) return [];
    return (this.structure?.sections ?? []).filter(s => this.includedSectionIds.has(s.id));
  }

  /** Version code for the review card — works in blank mode too, where the catalog structure never loads. */
  get selectedVersionCode(): string {
    return ( this.versions.find(v => v.id === this.selectedVersionId)?.code
      ?? this.structure?.versionCode
      ?? ''
    );
  }

  /** Topic tabs for step 3: included catalog topics + every custom topic. */
  get topicTabs(): { key: number; title: string; isCustom: boolean; selected: number; total: number; }[] {
    const catalog = this.includedCatalogSections.map(s => ({
      key: s.id,
      title: s.title,
      isCustom: false,
      selected: this.selectedInSection(s) + (this.customQuestionsBySection[s.id]?.length ?? 0),
      total: s.items.length + (this.customQuestionsBySection[s.id]?.length ?? 0)
    }));
    const custom = this.customTopics.map(t => ({
      key: t.tempId,
      title: t.title,
      isCustom: true,
      selected: t.questions.length,
      total: t.questions.length
    }));
    return [...catalog, ...custom];
  }

  get activeCatalogSection(): FlexiComplianceCatalogSection | null {
    if (this.activeTopicKey === null || this.activeTopicKey < 0) return null;
    return ( this.includedCatalogSections.find(s => s.id === this.activeTopicKey) ?? null
    );
  }

  get activeCustomTopic(): DraftTopic | null {
    if (this.activeTopicKey === null || this.activeTopicKey >= 0) return null;
    return ( this.customTopics.find(t => t.tempId === this.activeTopicKey) ?? null
    );
  }

  activeCustomQuestions(sectionId: number): DraftQuestion[] {
    return this.customQuestionsBySection[sectionId] ?? [];
  }

  selectedInSection(s: FlexiComplianceCatalogSection): number {
    return s.items.filter(i => this.includedItemIds.has(i.id)).length;
  }

  get totalSelectedQuestions(): number {
    const catalogPicked = this.includedCatalogSections
      .reduce((sum, s) => sum + this.selectedInSection(s), 0);
    const catalogAuthored = this.includedCatalogSections
      .reduce((sum, s) => sum + (this.customQuestionsBySection[s.id]?.length ?? 0), 0);
    const customAuthored = this.customTopics.reduce((sum, t) => sum + t.questions.length, 0);
    return catalogPicked + catalogAuthored + customAuthored;
  }

  get totalTopics(): number {
    return (
      (this.isBlank ? 0 : this.includedSectionIds.size) + this.customTopics.length
    );
  }

  get canNext(): boolean {
    switch (this.step) {
      case 0: return this.name.trim().length > 0 && this.selectedVersionId !== null;
      case 1: return this.totalTopics > 0;
      case 2: return this.totalSelectedQuestions > 0;
      default: return true;
    }
  }

  // ---------- Navigation ----------

  next(): void {
    if (!this.canNext || this.saving) return;
    this.error = null;
    if (this.step === 0 && !this.isBlank) {
      if (!this.structure || this.structure.versionId !== this.selectedVersionId) {
        this.loadStructure(() => { this.step = 1; });
        return;
      }
    }
    if (this.step === 1) {
      // Land on the first available topic tab when entering questions.
      const tabs = this.topicTabs;
      if (this.activeTopicKey === null || !tabs.some(t => t.key === this.activeTopicKey)) {
        this.activeTopicKey = tabs[0]?.key ?? null;
      }
    }
    this.step = Math.min(this.step + 1, this.lastStep);
  }

  back(): void {
    if (this.saving) return;
    this.error = null;
    this.step = Math.max(this.step - 1, 0);
  }

  cancel(): void {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  private loadStructure(then: () => void): void {
    if (this.selectedVersionId === null) return;
    this.structureLoading = true;
    this.service.getCatalogStructure(this.selectedVersionId).subscribe({
      next: structure => {
        this.structure = structure;
        // Default: everything included — the prune flow's starting point.
        this.includedSectionIds = new Set(structure.sections.map(s => s.id));
        this.includedItemIds = new Set(structure.sections.flatMap(s => s.items.map(i => i.id)));
        this.structureLoading = false;
        then();
      },
      error: (err: HttpErrorResponse) => {
        this.structureLoading = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.WIZARD.LOAD_STRUCTURE_FAILED');
      }
    });
  }

  // ---------- Catalog picking ----------

  toggleSection(s: FlexiComplianceCatalogSection): void {
    if (this.includedSectionIds.has(s.id)) {
      this.includedSectionIds.delete(s.id);
      for (const item of s.items) this.includedItemIds.delete(item.id);
      // Authored questions under a dropped topic go too — review counts
      // must stay truthful.
      delete this.customQuestionsBySection[s.id];
    } else {
      this.includedSectionIds.add(s.id);
      for (const item of s.items) this.includedItemIds.add(item.id);
    }
  }

  toggleItem(itemId: number): void {
    if (this.includedItemIds.has(itemId)) this.includedItemIds.delete(itemId);
    else this.includedItemIds.add(itemId);
  }

  setActiveTopic(key: number): void {
    this.activeTopicKey = key;
    this.closeQuestionForm();
  }

  // ---------- Custom topics ----------

  openAddTopic(): void { this.addTopicOpen = true; this.addTopicTitle = ''; }
  cancelAddTopic(): void { this.addTopicOpen = false; this.addTopicTitle = ''; }

  submitAddTopic(): void {
    const title = this.addTopicTitle.trim();
    if (!title) return;
    this.customTopics.push({ tempId: this.tempIdSeq--, title, questions: [] });
    this.cancelAddTopic();
  }

  openRenameTopic(t: DraftTopic): void {
    this.renameTopicTempId = t.tempId;
    this.renameTopicTitle = t.title;
  }
  cancelRenameTopic(): void { this.renameTopicTempId = null; this.renameTopicTitle = ''; }

  submitRenameTopic(): void {
    const title = this.renameTopicTitle.trim();
    if (this.renameTopicTempId === null || !title) return;
    const topic = this.customTopics.find(t => t.tempId === this.renameTopicTempId);
    if (topic) topic.title = title;
    this.cancelRenameTopic();
  }

  removeCustomTopic(tempId: number): void {
    this.customTopics = this.customTopics.filter(t => t.tempId !== tempId);
    if (this.activeTopicKey === tempId) this.activeTopicKey = null;
  }

  // ---------- Custom questions (under catalog AND custom topics) ----------

  openAddQuestion(): void {
    this.questionFormOpen = true;
    this.editingQuestionTempId = null;
    this.questionDraft = { tempId: this.tempIdSeq--, question: '', ref: '', weight: 1 };
  }

  openEditQuestion(q: DraftQuestion): void {
    this.questionFormOpen = true;
    this.editingQuestionTempId = q.tempId;
    this.questionDraft = { ...q };
  }

  closeQuestionForm(): void {
    this.questionFormOpen = false;
    this.editingQuestionTempId = null;
  }

  submitQuestion(): void {
    const text = this.questionDraft.question.trim();
    if (!text || this.activeTopicKey === null) return;

    const list = this.activeTopicKey < 0
      ? this.customTopics.find(t => t.tempId === this.activeTopicKey)?.questions
      : (this.customQuestionsBySection[this.activeTopicKey] ??= []);
    if (!list) return;

    if (this.editingQuestionTempId !== null) {
      const existing = list.find(q => q.tempId === this.editingQuestionTempId);
      if (existing) {
        existing.question = text;
        existing.ref = this.questionDraft.ref.trim();
        existing.weight = this.questionDraft.weight || 1;
      }
    } else {
      list.push({
        tempId: this.questionDraft.tempId,
        question: text,
        ref: this.questionDraft.ref.trim(),
        weight: this.questionDraft.weight || 1
      });
    }
    this.closeQuestionForm();
  }

  removeDraftQuestion(q: DraftQuestion): void {
    if (this.activeTopicKey === null) return;
    if (this.activeTopicKey < 0) {
      const topic = this.customTopics.find(t => t.tempId === this.activeTopicKey);
      if (topic) topic.questions = topic.questions.filter(x => x.tempId !== q.tempId);
    } else {
      const list = this.customQuestionsBySection[this.activeTopicKey];
      if (list) {
        this.customQuestionsBySection[this.activeTopicKey] = list.filter(x => x.tempId !== q.tempId);
      }
    }
    if (this.editingQuestionTempId === q.tempId) this.closeQuestionForm();
  }

  // ---------- Save ----------

  create(): void {
    if (this.saving) return;
    if (this.isDuplicating) {
      this.duplicateFromSource();
      return;
    }
    if (this.selectedVersionId === null) return;
    this.saving = true;
    this.error = null;

    // Blank mode sends an EMPTY list (= seed no catalog items); the prune
    // flow sends the explicit picks.
    const selectedIds = this.isBlank
      ? []
      : this.includedCatalogSections
          .flatMap(s => s.items.filter(i => this.includedItemIds.has(i.id)).map(i => i.id));

    const customItems = this.isBlank
      ? []
      : this.includedCatalogSections.flatMap(s =>
          (this.customQuestionsBySection[s.id] ?? []).map(q => ({
            sectionId: s.id,
            question: q.question,
            regulatoryReference: q.ref || null,
            weight: q.weight
          })));

    const body: CreateComplianceTemplateRequest = {
      name: this.name.trim(),
      description: this.description.trim() || null,
      catalogVersionId: this.selectedVersionId,
      selectedCatalogItemIds: selectedIds,
      customItems,
      customSections: this.customTopics.map(t => ({
        title: t.title,
        items: t.questions.map(q => ({
          question: q.question,
          regulatoryReference: q.ref || null,
          weight: q.weight
        }))
      }))
    };
    this.service.create(body).subscribe({
      next: created => {
        this.notifications.success('UI_NOTIFICATIONS.SAVED');
        this.saving = false;
        // Land in the editor for any final fine-tuning.
        this.router.navigate(['../', created.id], { relativeTo: this.route });
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.CREATE_FAILED');
      }
    });
  }

  /**
   * "Start from existing template": one server-side deep copy (custom
   * topics, questions, exclusions all carried over), optionally followed
   * by a metadata update when the user typed a description. Then straight
   * to the editor — the prune steps don't apply to a copy.
   */
  private duplicateFromSource(): void {
    const sourceId = this.source as number;
    this.saving = true;
    this.error = null;
    this.service.duplicate(sourceId, this.name.trim()).subscribe({
      next: clone => {
        const desc = this.description.trim();
        if (!desc) {
          this.saving = false;
          this.router.navigate(['../', clone.id], { relativeTo: this.route });
          return;
        }
        this.service.update(clone.id, {
          name: clone.name,
          description: desc,
          isActive: true
        }).subscribe({
          // Either way the clone exists — land in its editor; a failed
          // description update is recoverable there.
          next: () => {
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
            this.saving = false;
            this.router.navigate(['../', clone.id], { relativeTo: this.route });
          },
          error: () => {
            this.saving = false;
            this.router.navigate(['../', clone.id], { relativeTo: this.route });
          }
        });
      },
      error: (err: HttpErrorResponse) => {
        this.saving = false;
        this.error = err.error?.detail
          ?? this.translate.instant('FLEXI_COMPLIANCE_TEMPLATE.ERRORS.CREATE_FAILED');
      }
    });
  }
}
